/**
 * Output Formats API Service (KAN-84)
 * CRUD for output format templates, source schema, and file generation.
 */

import { apiClient, APIResponse } from './client';
import { auth } from '../../config/firebase';
import type {
  OutputFormatTemplate,
  OutputFormatTemplateSummary,
  SourceSchema,
  GenerateRequest,
} from '../../types/outputFormats';


class OutputFormatsService {

  // ========== Template CRUD ==========

  async listTemplates(
    clientId: string,
    sourceType?: string,
  ): Promise<OutputFormatTemplateSummary[]> {
    const qs = sourceType ? `?sourceType=${sourceType}` : '';
    const resp = await apiClient.get<OutputFormatTemplateSummary[]>(
      `/api/v1/output-formats/${clientId}/templates${qs}`
    );
    return resp.data;
  }

  async getTemplate(
    clientId: string,
    templateId: string,
  ): Promise<OutputFormatTemplate> {
    const resp = await apiClient.get<OutputFormatTemplate>(
      `/api/v1/output-formats/${clientId}/templates/${templateId}`
    );
    return resp.data;
  }

  async createTemplate(
    clientId: string,
    template: Omit<OutputFormatTemplate, 'templateId'>,
  ): Promise<OutputFormatTemplate> {
    const resp = await apiClient.post<OutputFormatTemplate>(
      `/api/v1/output-formats/${clientId}/templates`,
      template
    );
    return resp.data;
  }

  async updateTemplate(
    clientId: string,
    templateId: string,
    template: Partial<OutputFormatTemplate>,
  ): Promise<OutputFormatTemplate> {
    const resp = await apiClient.put<OutputFormatTemplate>(
      `/api/v1/output-formats/${clientId}/templates/${templateId}`,
      template
    );
    return resp.data;
  }

  async deactivateTemplate(
    clientId: string,
    templateId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/api/v1/output-formats/${clientId}/templates/${templateId}`
    );
  }

  async duplicateTemplate(
    clientId: string,
    templateId: string,
  ): Promise<OutputFormatTemplate> {
    const resp = await apiClient.post<OutputFormatTemplate>(
      `/api/v1/output-formats/${clientId}/templates/${templateId}/duplicate`
    );
    return resp.data;
  }

  // ========== Source Schema ==========

  async getSourceSchema(
    clientId: string,
    sourceType: string,
    rowMode?: string,
  ): Promise<SourceSchema> {
    const qs = rowMode ? `?rowMode=${rowMode}` : '';
    const resp = await apiClient.get<SourceSchema>(
      `/api/v1/output-formats/${clientId}/source-schema/${sourceType}${qs}`
    );
    return resp.data;
  }

  // ========== File Generation ==========

  async generate(
    clientId: string,
    request: GenerateRequest,
  ): Promise<{ blob: Blob; filename: string }> {
    const baseUrl = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000';
    const token = await auth.currentUser?.getIdToken();

    const response = await fetch(
      `${baseUrl}/api/v1/output-formats/${clientId}/generate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to generate file' }));
      throw new Error(errorData.detail || errorData.message || 'Failed to generate file');
    }

    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^"]+)"?/);
    const filename = match?.[1] || `output_${new Date().toISOString().slice(0, 10)}.csv`;
    const blob = await response.blob();
    return { blob, filename };
  }
}

export const outputFormatsService = new OutputFormatsService();
export default outputFormatsService;
